import { type FC, Fragment, useState } from "react";
import { type User } from "../UserTable";
import { useFetchClientShops } from "./useFetchClientShops";
import { useFetchPrefectures } from "./useFetchPrefectures";
import { useAutoSelectWork } from "./useAutoSelectWork";
import { Presentation } from "./Presentation";
import { type Work } from "../WorkSelector/useWorksFilter";

type Props = {
  // 主要データ
  defaultSelectedUserIds: number[];
  users: User[];
  works: readonly Work[];
  totalWorksCount: number;
  scoutLimitDate: number;
  clientShopId?: number;

  // 初期値・初期選択
  defaultWorkId?: string;
  defaultClientShopOptions: readonly {
    value: number;
    label: string;
  }[];
  defaultPrefectureId: number | null;
  defaultPrefectureOptions: readonly {
    value: number;
    label: string;
  }[];

  // API
  fetchUserCassetteUrl: string;
  fetchUserCassetteParams: Record<string, unknown>;
  fetchClientShopsUrl: string;
  fetchPrefecturesUrl: string;
  autoSelectWorkPath: string;

  // 表示制御
  isSp?: boolean;
  isWorkSelectionRequired?: boolean;
  isPremiumScout?: boolean;
  isDefaultWorkSelected: boolean;
  isAutoSelectDisable: boolean;
  isAutoSelectorVisible?: boolean;
  hasMatchingJobs: boolean;
  isPrefectureSelectorVisible?: boolean;
};

const SCOUT_FORM_NAME_BASE = "form_client_frontend_scout";
const PREMIUM_SCOUT_FORM_NAME_BASE = "form_client_frontend_premium_scout";
const OVER_MAX_SELECTED_USERS = 201;

const getInitialWorkId = (defaultWorkId: string | undefined, isAutoSelectDisable: boolean) => {
  if (defaultWorkId === "auto" && isAutoSelectDisable) {
    return undefined;
  }
  return defaultWorkId;
};

export const ScoutTargetAndWorkSelector: FC<Props> = ({
  defaultSelectedUserIds,
  users,
  works,
  totalWorksCount,
  scoutLimitDate,
  clientShopId = undefined,
  defaultWorkId,
  defaultClientShopOptions,
  defaultPrefectureId,
  defaultPrefectureOptions,
  fetchUserCassetteUrl,
  fetchUserCassetteParams,
  fetchClientShopsUrl,
  fetchPrefecturesUrl,
  autoSelectWorkPath,
  isSp = false,
  isWorkSelectionRequired = true,
  isPremiumScout = false,
  isDefaultWorkSelected,
  isAutoSelectDisable,
  isAutoSelectorVisible = false,
  hasMatchingJobs,
  isPrefectureSelectorVisible = false,
}) => {
  const { currentPrefectureId, prefectureOptions, fetchPrefectures, handlePrefectureChange } =
    useFetchPrefectures(fetchPrefecturesUrl, defaultPrefectureOptions, defaultPrefectureId);
  const { currentClientShopId, clientShopOptions, fetchClientShops, handleClientShopChange } =
    useFetchClientShops(fetchClientShopsUrl, defaultClientShopOptions, clientShopId);

  // state
  const [selectedUsers, setSelectedUsers] = useState<number[]>(defaultSelectedUserIds);
  const [currentWorkId, setCurrentWorkId] = useState<string | undefined>(
    getInitialWorkId(defaultWorkId, isAutoSelectDisable),
  );
  const { autoSelectStatus, autoSelectedWorkIds, executeAutoSelect, resetAutoSelectState } =
    useAutoSelectWork(autoSelectWorkPath, selectedUsers);

  const formNameBase = isPremiumScout ? PREMIUM_SCOUT_FORM_NAME_BASE : SCOUT_FORM_NAME_BASE;
  const isUserSelectionBundled = defaultSelectedUserIds.length > 1;
  const shopOfSelectedWork = works.find(({ value }) => value === currentWorkId)?.shops || [];
  const currentPrefectureName =
    prefectureOptions.find((pref) => pref.value === currentPrefectureId)?.label || "";
  const shopLengthOfPrefecture =
    works
      .find(({ value }) => value === currentWorkId)
      ?.shops.filter((shop) => shop.prefecture === currentPrefectureName).length || 0;

  const autoSelectDisabledMessage = (() => {
    if (isAutoSelectDisable) return "選択中の求職者にマッチする求人が無いため、選択できません。";
    if (selectedUsers.length >= OVER_MAX_SELECTED_USERS) {
      return `${OVER_MAX_SELECTED_USERS}人以上を選択すると、ご利用いただけません。`;
    }
    if (selectedUsers.length === 0) return "求職者を選択してください。";
    return undefined;
  })();

  // handlers
  const handleSelectedUserChange = (values: number[]) => {
    setSelectedUsers(values);
    // おまかせ実行済みの状態で割当の無い求職者が追加された場合は、おまかせ選択を解除して再実行を促す
    // （割当は求職者ごとに独立しているため、求職者を外しただけなら維持できる）
    if (
      currentWorkId === "auto" &&
      autoSelectStatus === "completed" &&
      values.some((userId) => !(String(userId) in autoSelectedWorkIds))
    ) {
      setCurrentWorkId(undefined);
      resetAutoSelectState();
    }
  };
  const handleChangeWork = (workId: string | undefined) => {
    setCurrentWorkId(workId);
    fetchPrefectures(workId);
    fetchClientShops(workId);
  };
  const handleAutoSelect = async () => {
    handleChangeWork("auto");
    const result = await executeAutoSelect();
    if (result.status === "failure") {
      handleChangeWork(undefined);
    }
  };
  const handleSelfSelect = () => {
    handleChangeWork(undefined);
    resetAutoSelectState();
  };

  return (
    <>
      {/* TODO:以下はスカウト送信画面全体をReact化する際に削除する */}
      {/* 求職者の選択状況 */}
      {selectedUsers.map((user) => (
        <input
          key={user}
          type="hidden"
          multiple
          name={
            isPremiumScout ? `${formNameBase}[user_id]` : `${formNameBase}[selected_user_ids][]`
          }
          value={user}
          id={`${formNameBase}_selected_user_ids_${user}`}
          className="js_check_scout_user js-scout-user-id"
        />
      ))}
      {/* 店舗と都道府県、求人の選択状況 */}
      <input
        type="hidden"
        name={`${formNameBase}[work_id]`}
        id={`${formNameBase}_work_id`}
        value={currentWorkId ?? ""}
        className="js-scout-select-work-id"
      />
      {/* 一括スカウト送信用の求職者ごとの求人割当（手動選択時は全員同一の求人、おまかせはユーザーごとの割当、マッチなしは空文字） */}
      {/* NOTE: 描画条件はサーバ側の一括/単発判定（multiple_scout hidden）と同じく初期選択人数由来の isUserSelectionBundled に揃える */}
      {!isPremiumScout &&
        isUserSelectionBundled &&
        selectedUsers.map((user) => (
          <Fragment key={user}>
            <input
              type="hidden"
              name={`${formNameBase}[user_and_work_list][][user_id]`}
              value={user}
            />
            <input
              type="hidden"
              name={`${formNameBase}[user_and_work_list][][work_id]`}
              value={
                currentWorkId === "auto"
                  ? (autoSelectedWorkIds[String(user)] ?? "")
                  : (currentWorkId ?? "")
              }
            />
          </Fragment>
        ))}
      <input
        type="hidden"
        name={`${formNameBase}[client_shop_id]`}
        id={`${formNameBase}_client_shop_id`}
        value={currentClientShopId ?? ""}
        className="js-scout-select-client-shop-id"
      />
      <input
        type="hidden"
        name={`${formNameBase}[scout_prefecture_id]`}
        id={`${formNameBase}_prefecture_id`}
        value={currentPrefectureId ?? ""}
        className="js-scout-prefecture-select"
      />
      <Presentation
        isSp={isSp}
        isUserSelectionBundled={isUserSelectionBundled}
        hasMatchingJobs={hasMatchingJobs}
        isPremiumScout={isPremiumScout}
        isDefaultWorkSelected={isDefaultWorkSelected}
        isPrefectureSelectorVisible={isPrefectureSelectorVisible}
        fetchUserCassetteUrl={fetchUserCassetteUrl}
        fetchUserCassetteParams={fetchUserCassetteParams}
        selectedUserIds={selectedUsers}
        users={users}
        works={works}
        totalWorksCount={totalWorksCount}
        currentWorkId={currentWorkId}
        isWorkSelectionRequired={isWorkSelectionRequired}
        isAutoSelectorVisible={isAutoSelectorVisible}
        autoSelectDisabledMessage={autoSelectDisabledMessage}
        autoSelect={{
          status: autoSelectStatus,
          onAutoSelect: handleAutoSelect,
          onSelfSelect: handleSelfSelect,
        }}
        scoutLimitDate={scoutLimitDate}
        shopOfSelectedWork={shopOfSelectedWork}
        clientShopId={currentClientShopId}
        clientShopOptions={clientShopOptions}
        prefectureId={currentPrefectureId}
        prefectureOptions={prefectureOptions}
        shopLengthOfPrefecture={shopLengthOfPrefecture}
        onSelectedUserChange={handleSelectedUserChange}
        onWorkChange={handleChangeWork}
        onClientShopChange={handleClientShopChange}
        onPrefectureChange={handlePrefectureChange}
      />
    </>
  );
};
